"use client";

import { useEffect, useState } from "react";
import { Users, Mail, Phone, Building2, Briefcase, Star, Edit, Trash2, Save, FileText, Calendar } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { notify, confirmDialog } from "@/components/ui/dialogs";
import { post, put, del, can } from "@/utils/service";
import { apiMessage, fieldErrors, shortDate } from "@/utils/api";
import FloatingDrawer, { DrawerHeader, DetailRow, darkInput, darkLabel, darkPrimary, darkSecondary, darkDanger } from "@/components/crm/FloatingDrawer";
import { useCounts, countText } from "@/components/crm/useCounts";
import ListShell, { toolbarInput, pill, Avatar, stop } from "@/components/crm/ListShell";
import RecordSelect, { type Picked } from "@/components/crm/RecordSelect";
import { useRemoteList } from "@/components/crm/useRemoteList";
import AppSelect from "@/components/ui/app-select";
import SearchInput from "@/components/ui/search-input";

interface Contact {
  id: string;
  first_name: string;
  last_name: string | null;
  name: string;
  job_title: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  status: string;
  is_primary: boolean;
  description: string | null;
  account_id: string | null;
  account?: { id: string; name: string } | null;
  owner?: { name: string } | null;
  created_at: string;
  can?: { update: boolean; delete: boolean } | null;
}

const empty = { first_name: "", last_name: "", job_title: "", email: "", phone: "", mobile: "", status: "active", is_primary: false, description: "" };

function ContactForm({ isOpen, onClose, onSaved, contact }: { isOpen: boolean; onClose: () => void; onSaved: () => void; contact: Contact | null }) {
  const [form, setForm] = useState(empty);
  const [account, setAccount] = useState<Picked>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    if (contact) {
      setForm({
        first_name: contact.first_name, last_name: contact.last_name ?? "", job_title: contact.job_title ?? "", email: contact.email ?? "",
        phone: contact.phone ?? "", mobile: contact.mobile ?? "", status: contact.status, is_primary: contact.is_primary, description: contact.description ?? "",
      });
      setAccount(contact.account ? { id: contact.account.id, label: contact.account.name } : null);
    } else {
      setForm(empty);
      setAccount(null);
    }
  }, [isOpen, contact]);

  const set = (k: keyof typeof empty, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    setErrors({});
    const body = Object.fromEntries(Object.entries({ ...form, account_id: account?.id ?? null }).map(([k, v]) => [k, v === "" ? null : v]));
    try {
      if (contact) await put(`/contacts/${contact.id}`, body);
      else await post("/contacts", body);
      notify(contact ? "Contact updated" : "Contact created");
      onSaved();
      onClose();
    } catch (e) {
      setErrors(fieldErrors(e));
      notify(apiMessage(e, "Could not save the contact."));
    } finally {
      setBusy(false);
    }
  };

  const err = (k: string) => errors[k] && <p className="mt-1 text-xs text-red-400">{errors[k]}</p>;

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <DrawerHeader title={contact ? "Edit Contact" : "Add New Contact"} subtitle="A person you work with, usually at one of your accounts" />
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><label className={darkLabel}>First Name *</label><input className={darkInput} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />{err("first_name")}</div>
          <div><label className={darkLabel}>Last Name</label><input className={darkInput} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} /></div>
        </div>
        <div><label className={darkLabel}>Account</label><RecordSelect kind="accounts" value={account} onChange={setAccount} placeholder="Search accounts…" />{err("account_id")}</div>
        <div><label className={darkLabel}>Job Title</label><input className={darkInput} value={form.job_title} onChange={(e) => set("job_title", e.target.value)} /></div>
        <div><label className={darkLabel}>Email</label><input type="email" className={darkInput} value={form.email} onChange={(e) => set("email", e.target.value)} />{err("email")}</div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={darkLabel}>Phone</label><input type="tel" className={darkInput} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          <div><label className={darkLabel}>Mobile</label><input type="tel" className={darkInput} value={form.mobile} onChange={(e) => set("mobile", e.target.value)} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={darkLabel}>Status</label>
            <AppSelect className={darkInput} value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </AppSelect>
          </div>
          <label className="flex items-end gap-2 pb-2 text-xs text-white">
            <input type="checkbox" checked={form.is_primary} onChange={(e) => set("is_primary", e.target.checked)} className="h-4 w-4 accent-primary" />
            Main contact of the account
          </label>
        </div>
        <div><label className={darkLabel}>Notes</label><textarea rows={3} className={`${darkInput} resize-none`} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
      </div>
      <div className="p-6 border-t border-gray-800 space-y-3">
        <button onClick={submit} disabled={busy || !form.first_name} className={darkPrimary}><Save className="w-4 h-4" />{busy ? "Saving..." : contact ? "Update Contact" : "Create Contact"}</button>
        <button onClick={onClose} className={darkSecondary}>Cancel</button>
      </div>
    </FloatingDrawer>
  );
}

export default function ContactsPage() {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("first_name");
  const list = useRemoteList<Contact>("/contacts", { search: query, status, sort });
  const { counts, loading: countsLoading } = useCounts("/contacts", { all: {}, active: { status: "active" }, inactive: { status: "inactive" }, mine: { owner: "me" } }, list.rows.length);
  const [selected, setSelected] = useState<Contact | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);

  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const remove = async (c: Contact) => {
    if (!await confirmDialog(`Delete ${c.name}? Their activity history is kept on related records.`)) return;
    try {
      await del(`/contacts/${c.id}`);
      notify("Contact deleted");
      setSelected(null);
      list.reload();
    } catch (e) {
      notify(apiMessage(e, "Could not delete the contact."));
    }
  };


  const card = (c: Contact) => (
    <div className="flex h-full flex-col">
      <div className="flex items-start gap-3">
        <Avatar name={c.name} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{c.name}{c.is_primary && <Star className="ml-1 inline h-3 w-3 text-primary" />}</p>
          <p className="truncate text-xs text-gray-500">{[c.job_title, c.account?.name].filter(Boolean).join(" · ") || "No account"}</p>
        </div>
        <span className={pill(c.status === "active" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700")}>{c.status === "active" ? "Active" : "Inactive"}</span>
      </div>
      <div className="mt-4 space-y-1.5 text-xs text-gray-600">
        <p className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 shrink-0 text-gray-400" />{c.email ? <a href={`mailto:${c.email}`} onClick={stop} className="truncate hover:text-primary">{c.email}</a> : "—"}</p>
        <p className="flex items-center gap-2 truncate"><Phone className="h-3.5 w-3.5 shrink-0 text-gray-400" />{c.phone || c.mobile ? <a href={`tel:${c.phone ?? c.mobile}`} onClick={stop} className="hover:text-primary">{c.phone ?? c.mobile}</a> : "—"}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Contacts" description="The people at your accounts" />

        <ListShell
          renderCard={card}
          rowKey={(c) => c.id}
          rows={list.rows}
          loading={list.loading}
          pagination={list.pagination}
          onPage={list.setPage}
          noun="contacts"
          onRowClick={setSelected}
          onAdd={can("contacts.create") ? () => { setEditing(null); setFormOpen(true); } : undefined}
          addLabel="Add Contact"
          onPerPage={list.setPerPage}
          grid={{
            id: "contacts",
            search: <SearchInput className="w-full sm:w-64" placeholder="Search name, email or phone…" value={search} onChange={(e) => setSearch(e.target.value)} />,
            filters: (
              <label className="text-xs text-gray-600">Status
                <AppSelect className={`${toolbarInput} mt-1`} value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option>
                </AppSelect>
              </label>
            ),
            activeFilters: status ? 1 : 0,
            onClearFilters: () => setStatus(""),
            sortOptions: [{ value: "first_name", label: "First name" }, { value: "last_name", label: "Last name" }, { value: "-created_at", label: "Newest" }, { value: "created_at", label: "Oldest" }],
            sort, onSort: setSort,
            stats: [
              { label: "All contacts", value: countText(counts.all) },
              { label: "Active", value: countText(counts.active), tone: "good" },
              { label: "Inactive", value: countText(counts.inactive) },
              { label: "Owned by me", value: countText(counts.mine) },
            ],
            statsLoading: countsLoading,
            exportEntity: "contacts", exportPermission: "contacts.export",
          }}
          empty={{ icon: Users, title: query || status ? "No contacts match" : "No contacts yet", text: query || status ? "Try another search or filter." : "Add the people you work with, or convert a lead to create one.", action: "Add First Contact" }}
        />
      </div>

      <FloatingDrawer isOpen={!!selected} onClose={() => setSelected(null)}>
        {selected && (
          <>
            <DrawerHeader title="Contact Details" subtitle={selected.name} />
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <DetailRow icon={Building2} label="Account">{selected.account?.name ?? "—"}</DetailRow>
              <DetailRow icon={Briefcase} label="Job Title">{selected.job_title ?? "—"}</DetailRow>
              <DetailRow icon={Mail} label="Email">{selected.email ? <a href={`mailto:${selected.email}`} className="text-primary">{selected.email}</a> : "—"}</DetailRow>
              <DetailRow icon={Phone} label="Phone">{[selected.phone, selected.mobile].filter(Boolean).join(" · ") || "—"}</DetailRow>
              <DetailRow icon={Calendar} label="Added">{shortDate(selected.created_at)}{selected.owner ? ` · owned by ${selected.owner.name}` : ""}</DetailRow>
              {selected.description && <DetailRow icon={FileText} label="Notes"><span className="whitespace-pre-line">{selected.description}</span></DetailRow>}
            </div>
            <div className="p-3 border-t border-gray-800 grid grid-cols-2 gap-2">
              <button disabled={selected.can?.update === false} onClick={() => { setEditing(selected); setSelected(null); setFormOpen(true); }} className={darkPrimary}><Edit className="w-4 h-4" />Edit</button>
              <button disabled={selected.can?.delete === false} onClick={() => remove(selected)} className={darkDanger}><Trash2 className="w-4 h-4" />Delete</button>
            </div>
          </>
        )}
      </FloatingDrawer>

      <ContactForm isOpen={formOpen} onClose={() => setFormOpen(false)} onSaved={list.reload} contact={editing} />
    </div>
  );
}
